import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { ProductType } from '@core/models/api/dsm-types';
import { ProductsService } from '@core/services/products.service';
import {
  PersonaReport,
  PersonaRecommendation,
  PersonaRecommendationIdToDisplayLabelAndIcon,
  PersonaRecommendationId,
  personaPackageFocusContent,
  personaChoices,
} from '@entities/persona/persona.model';
import { ProductUtils } from '@shared/utils/product.util';
import { Nullable } from '@shared/utils/type.utils';
import { Observable, Subject, takeUntil } from 'rxjs';
import { PersonaHelpModalComponent } from '../persona-help-modal/persona-help-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'nwx-package-version-details',
    templateUrl: './package-version-details.component.html',
    styleUrls: ['./package-version-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PackageVersionDetailsComponent implements OnChanges, OnDestroy {
  @Input() isProtectorVersionTabActive = false;
  @Input() personaReport: Nullable<PersonaReport> = {
    name: 'None',
    isManualSelection: false,
    recommendations: [],
  };

  products!: string[];
  selectedProductTypes$!: Observable<ProductType[]>;
  selectedPropertyProductType!: ProductType | undefined;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private productsService: ProductsService,
    private changeDetector: ChangeDetectorRef,
    private modal: NgbModal
  ) {
    this.selectedProductTypes$ = this.productsService.getSelectedProductTypes();
    this.selectedProductTypes$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedProducts: ProductType[]) => {
        this.selectedPropertyProductType = selectedProducts.find(
          (productType) => ProductUtils.isPropertyProduct(productType)
        );
        this.changeDetector.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.personaReport && !!this.personaReport && this.personaReport.recommendations?.length) {
      this.products = [
        ...new Set(
          this.personaReport.recommendations?.map((rec) => rec.product)
        ),
      ];
    }

    if (!this.selectedPropertyProductType) {
      this.products = this.products.filter((p) => p !== 'Property');
    }

    if (this.products?.length) {
      const preferredDisplayOrder = ['Property', 'Auto', ''];
      this.products.sort(
        (a, b) =>
          preferredDisplayOrder.indexOf(a) - preferredDisplayOrder.indexOf(b)
      );
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getProductDisplayName(personaProductType: string): string {
    if (personaProductType === 'Property' && this.selectedPropertyProductType) {
      return this.selectedPropertyProductType;
    }
    if (!personaProductType.length) {
      return 'More';
    }
    return personaProductType;
  }

  selectedPersonaDescription(): string {
    return personaPackageFocusContent(this.personaReport?.name);
  }

  getProductRecommendations(product: string): PersonaRecommendation[] {
    if (!this.personaReport?.recommendations) return [];
    return this.personaReport.recommendations
      .filter(
        (rec) =>
          rec.id !== 'ValuablesPlus' &&
          rec.id !== 'Umbrella' &&
          rec.id !== 'Auto'
      )
      .filter((rec) => rec.product === product);
  }

  hasRecommendationsForProduct(product: string): boolean {
    return this.getProductRecommendations(product).length > 0;
  }

  getRecommendationDisplayDetails(
    id: PersonaRecommendationId
  ): { label: string; icon: string } | undefined {
    if (id === 'RoadsidePlus') {
      return { 
        ...PersonaRecommendationIdToDisplayLabelAndIcon[id],
        label: PersonaRecommendationIdToDisplayLabelAndIcon[id].label + ' (Plus)' 
      };
    }
    return PersonaRecommendationIdToDisplayLabelAndIcon[id];
  }

  getProductIconName(product: string): string {
    switch (product) {
      case 'Auto':
        return 'city-car.svg';
      case 'Property':
        return 'house.svg';
      case '':
        return 'more-options.svg';
      default:
        return '';
    }
  }

  isRecommendationPresent(recommendation: PersonaRecommendation): boolean {
    return recommendation.present;
  }

  displayHelpModal(): void {
    if (this.personaReport) {
      const modal = this.modal.open(PersonaHelpModalComponent, {
        animation: false,
        size: 'xl',
        centered: true,
        backdrop: 'static',
        keyboard: false,
        scrollable: false,
        ariaLabelledBy: 'personaHelpModal',
      });
      modal.componentInstance.personaNameSelected = this.personaReport.name;
      modal.componentInstance.items = personaChoices.filter(choice => choice.enabled);
    }
  }
}